import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isDocumentedImageSize } from "@tandem/image-studio-shared";
import { api, ApiClientError } from "../api.js";
import { aspectRatio, statusLabel } from "../ui.js";
import type {
  Client,
  GenerationDefaults,
  PlatformPreset,
  PromptTemplateDetail,
  PromptTemplateVersion,
  TemplateCategory,
  TemplateScope,
  TemplateVariable,
  ValidationIssue,
} from "../types.js";
import { categories } from "./templates-screen.js";

export function TemplateEditorScreen() {
  const { templateId = "" } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<PromptTemplateDetail | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [presets, setPresets] = useState<PlatformPreset[]>([]);
  const [versions, setVersions] = useState<PromptTemplateVersion[]>([]);
  const [selectedVersion, setSelectedVersion] =
    useState<PromptTemplateVersion | null>(null);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [preview, setPreview] = useState("");
  const [previewValues, setPreviewValues] = useState<Record<string, string>>(
    Object.create(null) as Record<string, string>,
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const load = async () => {
    try {
      const [template, clientList, presetList, history] = await Promise.all([
        api.getTemplate(templateId),
        api.listClients(
          new URLSearchParams({ status: "active", pageSize: "100" }),
        ),
        api.listPlatformPresets(),
        api.listTemplateVersions(templateId),
      ]);
      setDetail(template);
      setClients(clientList.items);
      setPresets(
        presetList.items.filter((preset) =>
          isDocumentedImageSize(preset.width, preset.height),
        ),
      );
      setVersions(history);
      setIssues(template.validation.issues);
      setError("");
    } catch {
      setError("Не удалось загрузить редактор шаблона.");
    }
  };
  useEffect(() => {
    void load();
  }, [templateId]);
  const change = <K extends keyof PromptTemplateDetail>(
    key: K,
    value: PromptTemplateDetail[K],
  ) =>
    setDetail((current) => (current ? { ...current, [key]: value } : current));
  const changeDraft = <K extends keyof PromptTemplateDetail["draft"]>(
    key: K,
    value: PromptTemplateDetail["draft"][K],
  ) =>
    setDetail((current) =>
      current
        ? { ...current, draft: { ...current.draft, [key]: value } }
        : current,
    );
  const save = async () => {
    if (!detail) return;
    setMessage("");
    setError("");
    try {
      const saved = await api.saveTemplateDraft(
        detail.id,
        editorPayload(detail),
      );
      setDetail(saved);
      setIssues(saved.validation.issues);
      setMessage("Черновик сохранён");
    } catch (reason) {
      setError(errorMessage(reason));
    }
  };
  const validate = async () => {
    if (!detail) return;
    try {
      const result = await api.validateTemplate(detail.id);
      setIssues(result.issues);
      setMessage(result.valid ? "Шаблон прошёл проверку" : "Найдены ошибки");
    } catch (reason) {
      setError(errorMessage(reason));
    }
  };
  const runPreview = async () => {
    if (!detail) return;
    try {
      const values: Record<string, string | number | boolean | null> =
        Object.create(null) as Record<string, string | number | boolean | null>;
      for (const variable of detail.draft.variables.filter(
        (item) => item.source === "user",
      )) {
        const raw = previewValues[variable.key];
        if (raw === undefined || raw === "") continue;
        values[variable.key] =
          variable.type === "integer"
            ? Number(raw)
            : variable.type === "boolean"
              ? raw === "true"
              : raw;
      }
      const result = await api.previewTemplate(detail.id, {
        clientId: detail.clientId ?? clients[0]?.id ?? null,
        previewValues: values,
      });
      setPreview(result.text);
      setIssues(result.issues);
      setMessage(
        result.valid
          ? "Предпросмотр обновлён"
          : `Не заполнено: ${result.unresolved.join(", ") || "ошибка проверки"}`,
      );
    } catch (reason) {
      setError(errorMessage(reason));
    }
  };
  const publish = async () => {
    if (!detail) return;
    const validation = await api.validateTemplate(detail.id);
    setIssues(validation.issues);
    if (!validation.valid) {
      setMessage("Публикация заблокирована: исправьте ошибки");
      return;
    }
    try {
      const version = await api.publishTemplate(
        detail.id,
        detail.currentVersionNumber
          ? "Обновлённый черновик"
          : "Первая публикация",
      );
      setMessage(`Опубликована версия ${version.versionNumber}`);
      setSelectedVersion(null);
      await load();
    } catch (reason) {
      setError(errorMessage(reason));
    }
  };
  if (error && !detail)
    return (
      <section className="panel detail-loading form-error">{error}</section>
    );
  if (!detail)
    return (
      <section className="panel detail-loading">
        Загружаем редактор шаблона…
      </section>
    );
  const readonly = Boolean(selectedVersion);
  return (
    <section className="template-editor-page">
      <div className="panel template-editor-header">
        <div>
          <Link to="/templates">← Шаблоны</Link>
          <div className="template-name-row">
            <input
              aria-label="Имя шаблона"
              required
              disabled={readonly}
              value={detail.name}
              onChange={(event) => change("name", event.target.value)}
            />
            <b className={`status-pill ${detail.status}`}>
              {statusLabel(detail.status)}
            </b>
            {detail.hasUnpublishedDraft ? (
              <span className="draft-marker">
                есть неопубликованный черновик
              </span>
            ) : null}
          </div>
        </div>
        <div className="editor-actions">
          <button
            onClick={() => void save()}
            disabled={readonly || detail.status === "archived"}
          >
            Сохранить черновик
          </button>
          <button onClick={() => void validate()} disabled={readonly}>
            Проверить
          </button>
          <button
            className="primary"
            onClick={() => void publish()}
            disabled={readonly || detail.status === "archived"}
          >
            Опубликовать
          </button>
          <details className="editor-action-menu">
            <summary aria-label="Другие действия">⋯</summary>
            <div>
              <button onClick={() => setDuplicateOpen(true)}>
                Дублировать
              </button>
              <button
                className="danger-button"
                disabled={detail.status === "archived"}
                onClick={() =>
                  void api
                    .archiveTemplate(detail.id)
                    .then(() => navigate("/templates"))
                }
              >
                Архивировать
              </button>
            </div>
          </details>
        </div>
      </div>
      {message ? <div className="editor-notice">{message}</div> : null}
      {error ? <div className="form-error">{error}</div> : null}
      {readonly && selectedVersion ? (
        <VersionReadonly
          version={selectedVersion}
          onClose={() => setSelectedVersion(null)}
        />
      ) : (
        <div className="template-split">
          <main className="panel prompt-column">
            <div className="section-row">
              <div>
                <h2>Инструкция генерации</h2>
                <p>Основной текст шаблона и системные ограничения.</p>
              </div>
              <span className="char-count">
                {detail.draft.promptText.length}/50000
              </span>
            </div>
            <label>
              Текст инструкции
              <textarea
                aria-label="Текст инструкции"
                className="prompt-textarea"
                value={detail.draft.promptText}
                onChange={(event) =>
                  changeDraft("promptText", event.target.value)
                }
                placeholder="Create {{subject}} using {{brand.visual_style}}"
              />
            </label>
            <div className="insert-strip">
              <span>Вставить:</span>
              {detail.draft.variables.map((variable) => (
                <button
                  key={variable.key}
                  type="button"
                  onClick={() =>
                    changeDraft(
                      "promptText",
                      `${detail.draft.promptText}${detail.draft.promptText ? " " : ""}{{${variable.key}}}`,
                    )
                  }
                >{`{{${variable.key}}}`}</button>
              ))}
            </div>
            <label>
              Системные правила
              <textarea
                aria-label="Системные правила"
                rows={5}
                value={detail.draft.systemInstructions}
                onChange={(event) =>
                  changeDraft("systemInstructions", event.target.value)
                }
              />
            </label>
            <div className="preview-controls">
              <button
                className="primary compact-button"
                onClick={() => void runPreview()}
              >
                Обновить предпросмотр
              </button>
              {detail.draft.variables
                .filter((variable) => variable.source === "user")
                .map((variable) => (
                  <label key={variable.key}>
                    {variable.label}
                    <input
                      aria-label={`Предпросмотр ${variable.key}`}
                      value={previewValues[variable.key] ?? ""}
                      onChange={(event) =>
                        setPreviewValues({
                          ...previewValues,
                          [variable.key]: event.target.value,
                        })
                      }
                    />
                  </label>
                ))}
            </div>
            <section className="rendered-preview">
              <strong>Предпросмотр без обращения к модели</strong>
              <pre>
                {preview ||
                  "Заполните значения и нажмите «Обновить предпросмотр»."}
              </pre>
            </section>
            <ValidationPanel issues={issues} />
          </main>
          <aside className="editor-side">
            <TemplateMeta detail={detail} clients={clients} change={change} />
            <VariableEditor
              values={detail.draft.variables}
              onChange={(values) => changeDraft("variables", values)}
            />
            <DefaultsEditor
              values={detail.draft.generationDefaults}
              presets={presets}
              platformPresetId={detail.draft.platformPresetId}
              onChange={(values) => changeDraft("generationDefaults", values)}
              onPreset={(id) => changeDraft("platformPresetId", id)}
            />
            <VersionHistory
              versions={versions}
              current={detail.currentVersionNumber}
              onSelect={(version) =>
                void api
                  .getTemplateVersion(detail.id, version)
                  .then(setSelectedVersion)
              }
            />
          </aside>
        </div>
      )}
      {duplicateOpen ? (
        <DuplicateDialog
          template={detail}
          clients={clients}
          onClose={() => setDuplicateOpen(false)}
          onCreated={(id) => void navigate(`/templates/${id}`)}
        />
      ) : null}
    </section>
  );
}

function TemplateMeta({
  detail,
  clients,
  change,
}: {
  detail: PromptTemplateDetail;
  clients: Client[];
  change: <K extends keyof PromptTemplateDetail>(
    key: K,
    value: PromptTemplateDetail[K],
  ) => void;
}) {
  return (
    <section className="panel editor-box">
      <h3>Основное</h3>
      <label>
        Описание
        <textarea
          rows={2}
          value={detail.description}
          onChange={(event) => change("description", event.target.value)}
        />
      </label>
      <div className="form-grid">
        <label>
          Категория
          <select
            value={detail.category}
            onChange={(event) =>
              change("category", event.target.value as TemplateCategory)
            }
          >
            {categories.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Доступность
          <select
            aria-label="Доступность шаблона"
            value={detail.scope}
            onChange={(event) =>
              change("scope", event.target.value as TemplateScope)
            }
          >
            <option value="global">Общий</option>
            <option value="client">Для клиента</option>
          </select>
        </label>
      </div>
      {detail.scope === "client" ? (
        <label>
          Клиент
          <select
            aria-label="Клиент редактора"
            value={detail.clientId ?? ""}
            onChange={(event) => change("clientId", event.target.value)}
          >
            <option value="">Выберите</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </section>
  );
}

function VariableEditor({
  values,
  onChange,
}: {
  values: TemplateVariable[];
  onChange: (values: TemplateVariable[]) => void;
}) {
  const update = (index: number, patch: Partial<TemplateVariable>) =>
    onChange(
      values.map((value, position) =>
        position === index ? { ...value, ...patch } : value,
      ),
    );
  const add = () =>
    onChange([
      ...values,
      {
        key: `variable_${values.length + 1}`,
        label: "Новая переменная",
        type: "string",
        required: false,
        defaultValue: null,
        description: "",
        source: "user",
        displayOrder: values.length,
        enumOptions: [],
      },
    ]);
  return (
    <section className="panel editor-box variable-box">
      <div className="section-row">
        <div>
          <h3>Переменные · {values.length}</h3>
        </div>
        <button onClick={add}>＋</button>
      </div>
      {values.map((variable, index) => (
        <div className="variable-item" key={`${index}-${variable.key}`}>
          <div className="variable-top">
            <input
              aria-label={`Ключ переменной ${index + 1}`}
              value={variable.key}
              onChange={(event) => update(index, { key: event.target.value })}
            />
            <button
              aria-label={`Удалить ${variable.key}`}
              onClick={() =>
                onChange(values.filter((_, position) => position !== index))
              }
            >
              ×
            </button>
          </div>
          <input
            aria-label={`Название переменной ${index + 1}`}
            value={variable.label}
            onChange={(event) => update(index, { label: event.target.value })}
          />
          <div className="variable-controls">
            <select
              aria-label={`Тип переменной ${index + 1}`}
              value={variable.type}
              onChange={(event) =>
                update(index, {
                  type: event.target.value as TemplateVariable["type"],
                  enumOptions: event.target.value === "enum" ? ["option"] : [],
                })
              }
            >
              <option value="string">Строка</option>
              <option value="multiline_text">Многострочный текст</option>
              <option value="integer">Число</option>
              <option value="boolean">Переключатель</option>
              <option value="enum">Список</option>
            </select>
            <select
              aria-label={`Источник переменной ${index + 1}`}
              value={variable.source}
              onChange={(event) =>
                update(index, {
                  source: event.target.value as TemplateVariable["source"],
                  defaultValue: null,
                })
              }
            >
              <option value="user">Пользователь</option>
              <option value="client">Клиент</option>
              <option value="brand">Бренд</option>
            </select>
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              checked={variable.required}
              onChange={(event) =>
                update(index, {
                  required: event.target.checked,
                  defaultValue: event.target.checked
                    ? null
                    : variable.defaultValue,
                })
              }
            />{" "}
            Обязательная
          </label>
          {!variable.required && variable.source === "user" ? (
            <input
              aria-label={`Variable default ${index + 1}`}
              placeholder="Значение по умолчанию"
              value={
                variable.defaultValue === null
                  ? ""
                  : String(variable.defaultValue)
              }
              onChange={(event) =>
                update(index, {
                  defaultValue:
                    variable.type === "integer"
                      ? Number(event.target.value)
                      : variable.type === "boolean"
                        ? event.target.value === "true"
                        : event.target.value || null,
                })
              }
            />
          ) : null}
          {variable.type === "enum" ? (
            <input
              aria-label={`Enum options ${index + 1}`}
              value={variable.enumOptions.join(", ")}
              onChange={(event) =>
                update(index, {
                  enumOptions: event.target.value
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean),
                })
              }
              placeholder="red, blue"
            />
          ) : null}
          <input
            aria-label={`Variable description ${index + 1}`}
            value={variable.description}
            onChange={(event) =>
              update(index, { description: event.target.value })
            }
            placeholder="Описание"
          />
        </div>
      ))}
    </section>
  );
}

function DefaultsEditor({
  values,
  presets,
  platformPresetId,
  onChange,
  onPreset,
}: {
  values: GenerationDefaults;
  presets: PlatformPreset[];
  platformPresetId: string | null;
  onChange: (values: GenerationDefaults) => void;
  onPreset: (id: string | null) => void;
}) {
  const update = <K extends keyof GenerationDefaults>(
    key: K,
    value: GenerationDefaults[K],
  ) => onChange({ ...values, [key]: value });
  const flags: Array<[keyof GenerationDefaults, string]> = [
    ["useBrandProfile", "Использовать профиль бренда"],
    ["usePrimaryColors", "Фирменные цвета"],
    ["useStyleReferences", "Стилевые референсы"],
    ["useProductAssets", "Материалы продукции"],
    ["sendLogoAsReference", "Логотип как референс"],
    ["exactLogoOverlay", "Точное наложение логотипа"],
  ];
  return (
    <section className="panel editor-box">
      <h3>Параметры по умолчанию</h3>
      <div className="form-grid">
        <label>
          Количество
          <input
            aria-label="Количество изображений по умолчанию"
            type="number"
            min="1"
            max="20"
            value={values.imageCount}
            onChange={(event) =>
              update("imageCount", Number(event.target.value))
            }
          />
        </label>
        <label>
          Качество
          <select
            value={values.quality}
            onChange={(event) =>
              update(
                "quality",
                event.target.value as GenerationDefaults["quality"],
              )
            }
          >
            <option value="draft">Черновик</option>
            <option value="standard">Стандартное</option>
            <option value="maximum">Максимальное</option>
          </select>
        </label>
      </div>
      <label>
        Площадка
        <select
          aria-label="Площадка по умолчанию"
          value={platformPresetId ?? ""}
          onChange={(event) => onPreset(event.target.value || null)}
        >
          <option value="">Без площадки</option>
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.name} · {aspectRatio(preset)}
            </option>
          ))}
        </select>
      </label>
      <div className="default-flags">
        {flags.map(([key, label]) => (
          <label className="check-row" key={key}>
            <input
              type="checkbox"
              checked={Boolean(values[key])}
              onChange={(event) =>
                update(
                  key,
                  event.target.checked as GenerationDefaults[typeof key],
                )
              }
            />{" "}
            {label}
          </label>
        ))}
      </div>
      <label>
        Дополнительные правила
        <textarea
          rows={2}
          value={values.additionalRules}
          onChange={(event) => update("additionalRules", event.target.value)}
        />
      </label>
    </section>
  );
}

function ValidationPanel({ issues }: { issues: ValidationIssue[] }) {
  return (
    <section
      className={`validation-panel ${issues.length ? "invalid" : "valid"}`}
    >
      <strong>
        {issues.length ? `Проверка · ${issues.length}` : "Проверка пройдена"}
      </strong>
      {issues.map((issue, index) => (
        <span key={`${issue.code}-${index}`}>
          <b>{issue.path}</b>
          {issue.code}
        </span>
      ))}
    </section>
  );
}
function VersionHistory({
  versions,
  current,
  onSelect,
}: {
  versions: PromptTemplateVersion[];
  current: number | null;
  onSelect: (version: number) => void;
}) {
  return (
    <section className="panel editor-box">
      <h3>История версий</h3>
      <div className="version-list">
        {versions.map((version) => (
          <button
            key={version.id}
            onClick={() => onSelect(version.versionNumber)}
          >
            <b>
              v{version.versionNumber}
              {current === version.versionNumber ? " · текущая" : ""}
            </b>
            <span>{version.createdByName}</span>
            <small>{version.changelog || "Без описания изменений"}</small>
          </button>
        ))}
        {!versions.length ? (
          <small>Опубликованных версий пока нет.</small>
        ) : null}
      </div>
    </section>
  );
}
function VersionReadonly({
  version,
  onClose,
}: {
  version: PromptTemplateVersion;
  onClose: () => void;
}) {
  return (
    <section className="panel readonly-version">
      <div className="section-row">
        <div>
          <h2>Версия {version.versionNumber}</h2>
          <small>Историческая версия доступна только для чтения</small>
        </div>
        <button onClick={onClose}>Вернуться к черновику</button>
      </div>
      <p>
        {version.createdByName} ·{" "}
        {new Date(version.createdAt).toLocaleString("ru-RU")} ·{" "}
        {version.changelog || "Без описания изменений"}
      </p>
      <label>
        Инструкция
        <textarea readOnly value={version.promptText} rows={14} />
      </label>
      <label>
        Системные правила
        <textarea readOnly value={version.systemInstructions} rows={5} />
      </label>
      <pre>{version.checksum}</pre>
    </section>
  );
}

function DuplicateDialog({
  template,
  clients,
  onClose,
  onCreated,
}: {
  template: PromptTemplateDetail;
  clients: Client[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState(`${template.name} — копия`);
  const [scope, setScope] = useState<TemplateScope>(template.scope);
  const [clientId, setClientId] = useState(template.clientId ?? "");
  const [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      const created = await api.duplicateTemplate(template.id, {
        name,
        scope,
        ...(scope === "client" ? { clientId } : {}),
      });
      onCreated(created.id);
    } catch (reason) {
      setError(errorMessage(reason));
    }
  };
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog compact-dialog"
        aria-label="Дублировать шаблон"
        onSubmit={(event) => void submit(event)}
      >
        <div className="dialog-heading">
          <h2>Дублировать шаблон</h2>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <label>
          Новое имя
          <input
            aria-label="Имя копии"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label>
          Доступность
          <select
            value={scope}
            onChange={(event) => setScope(event.target.value as TemplateScope)}
          >
            <option value="global">Общий</option>
            <option value="client">Для клиента</option>
          </select>
        </label>
        {scope === "client" ? (
          <label>
            Клиент
            <select
              required
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
            >
              <option value="">Выберите</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary">Создать независимую копию</button>
      </form>
    </div>
  );
}
function editorPayload(detail: PromptTemplateDetail) {
  return {
    name: detail.name,
    description: detail.description,
    category: detail.category,
    scope: detail.scope,
    clientId: detail.scope === "client" ? detail.clientId : null,
    ...detail.draft,
  };
}
function errorMessage(reason: unknown) {
  return reason instanceof ApiClientError
    ? `${reason.code}: ${reason.message}`
    : "Не удалось выполнить действие.";
}
